/**
 * HubSalesPage — HUB-REDESIGN P5.
 *
 * Sales surface for hub.usetenon.com. Wires the design handoff at
 * /Users/joemeadows/Desktop/design_handoff_tenon_hub/other-pages.jsx
 * (SalesPage, lines 123-207) to real endpoints:
 *
 *   /hub/tenants            — trial/active counts, sandbox tally, trials
 *                             ending ≤7d, lead-funnel approximation
 *   /hub/business/metrics   — trial conversion %, MTD starts/conversions
 *
 * Design-side BACKEND_NEEDED tiles (Demos this wk, Avg cycle, Demo schedule
 * grid) render as `—` / empty cells with the BACKEND_NEEDED endpoint badge
 * — no fabricated demos. The illustrative "Northbeam / Rockford Drywall /
 * Axis Steel" entries from the design source are intentionally omitted.
 *
 * Refresh: tenants + business metrics every 30s.
 */
import React, { useEffect, useMemo, useState } from 'react'
import { Card, KPI, Endpoint, StatusDot, DepartmentObservability } from './components'
import {
  hubTenants, hubBusiness,
  type HubTenantsResponse, type HubTenantSummary, type HubBusinessMetrics,
} from './api'

const DAY_MS = 24 * 60 * 60 * 1000

export default function HubSalesPage() {
  const [tenants, setTenants] = useState<HubTenantsResponse | null>(null)
  const [metrics, setMetrics] = useState<HubBusinessMetrics | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const [t, m] = await Promise.all([
        hubTenants.list().catch(() => null),
        hubBusiness.metrics().catch(() => null),
      ])
      if (cancelled) return
      setTenants(t)
      setMetrics(m)
    }
    load()
    const id = setInterval(load, 30_000)
    return () => { cancelled = true; clearInterval(id) }
  }, [])

  const sandboxes = useMemo(() => pickSandboxes(tenants), [tenants])
  const trialsEndingSoon = useMemo(() => pickTrialsEndingSoon(tenants), [tenants])
  const funnel = useMemo(() => buildLeadFunnel(tenants), [tenants])
  const weekDays = useMemo(() => buildCurrentWeek(), [])

  return (
    <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <KPIStrip
        tenants={tenants}
        metrics={metrics}
        sandboxes={sandboxes}
        trialsEndingSoon={trialsEndingSoon}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 10 }}>
        <LeadFunnelTile funnel={funnel} />
        <TrialsEndingTile trials={trialsEndingSoon} />
        <DemoScheduleTile weekDays={weekDays} />
      </div>

      <DepartmentObservability department="sales" />
    </div>
  )
}

// ── KPI strip (5 cells, 1px border separators) ─────────────────────────
function KPIStrip({
  tenants, metrics, sandboxes, trialsEndingSoon,
}: {
  tenants: HubTenantsResponse | null
  metrics: HubBusinessMetrics | null
  sandboxes: HubTenantSummary[]
  trialsEndingSoon: TrialEntry[]
}) {
  const trialing = tenants?.summary.trialing ?? 0
  const endingCount = trialsEndingSoon.length
  const sandboxCount = sandboxes.length
  const sandboxLabel = sandboxes[0]?.company_name ?? 'none'
  const trialConvPct = metrics?.trial_conversion_rate_pct ?? 0
  const trialStarts = metrics?.trial_starts_mtd ?? 0
  const trialConv = metrics?.trial_conversions_mtd ?? 0

  // KPI molecule supports 'flat' | 'up' | 'down'. We map design "warn" →
  // 'down' (red/urgent) to surface trial-expiry urgency, and use 'up' when
  // conversion rate is healthy.
  return (
    <Card bodyPad={false} style={{ padding: 0 }}>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)',
        gap: 1, background: 'var(--border)',
      }}>
        <div style={{ background: 'var(--surface)' }}>
          <KPI
            label="Demos this wk"
            value="—"
            delta="no calendar wired yet"
            tone="flat"
            endpoint="BACKEND_NEEDED: calendar"
          />
        </div>
        <div style={{ background: 'var(--surface)' }}>
          <KPI
            label="Active trials"
            value={trialing}
            delta={`${endingCount} ending ≤7d`}
            tone={endingCount > 0 ? 'down' : 'flat'}
            endpoint="/hub/tenants"
          />
        </div>
        <div style={{ background: 'var(--surface)' }}>
          <KPI
            label="Sandboxes"
            value={sandboxCount}
            delta={sandboxLabel}
            tone="flat"
            endpoint="/hub/tenants"
          />
        </div>
        <div style={{ background: 'var(--surface)' }}>
          <KPI
            label="Trial→Paid"
            value={`${Math.round(trialConvPct)}%`}
            delta={`MTD ${trialConv}/${trialStarts}`}
            tone={trialConvPct >= 20 ? 'up' : 'flat'}
            endpoint="/hub/business/metrics"
          />
        </div>
        <div style={{ background: 'var(--surface)' }}>
          <KPI
            label="Avg cycle"
            value="—"
            delta="trial→paid"
            tone="flat"
            endpoint="BACKEND_NEEDED: cycle"
          />
        </div>
      </div>
    </Card>
  )
}

// ── Lead funnel · 30d (col span 7) ─────────────────────────────────────
type FunnelRow = { label: string; value: number }

function buildLeadFunnel(t: HubTenantsResponse | null): FunnelRow[] {
  if (!t) return []
  const s = t.summary
  return [
    { label: 'Lead', value: s.total },
    { label: 'Trial', value: s.trialing },
    { label: 'Active', value: s.active },
    { label: 'PQL', value: s.past_due },
    { label: 'Won', value: Math.max(0, s.active - s.past_due) },
  ]
}

function LeadFunnelTile({ funnel }: { funnel: FunnelRow[] }) {
  const max = funnel[0]?.value || 1
  return (
    <Card
      title="Lead funnel · 30d"
      endpoint="/hub/tenants"
      style={{ gridColumn: 'span 7' }}
    >
      {funnel.length === 0 && (
        <div style={{ color: 'var(--text-dim)', fontSize: 12, padding: '8px 0' }}>
          No tenant data yet.
        </div>
      )}
      {funnel.map((row, i) => {
        const pct = max ? (row.value / max) * 100 : 0
        return (
          <div key={i} className="row" style={{ gap: 10, padding: '5px 0' }}>
            <span style={{ width: 60, fontSize: 11.5, color: 'var(--text-muted)' }}>
              {row.label}
            </span>
            <div style={{
              flex: 1, height: 18, background: 'var(--surface-2)',
              borderRadius: 3, overflow: 'hidden',
            }}>
              <div style={{
                width: `${pct}%`, height: '100%',
                background: 'var(--accent)', opacity: 0.85,
              }} />
            </div>
            <span className="mono" style={{ fontSize: 12, width: 40, textAlign: 'right' }}>
              {row.value}
            </span>
            <span
              className="mono"
              style={{ fontSize: 10.5, width: 50, textAlign: 'right', color: 'var(--text-dim)' }}
            >
              {Math.round(pct)}%
            </span>
          </div>
        )
      })}
    </Card>
  )
}

// ── Trials ending ≤7d (col span 5) ─────────────────────────────────────
type TrialEntry = {
  tenant: HubTenantSummary
  daysRemaining: number // negative = expired
  label: string         // "expired 2d ago" | "today" | "in 3d"
  tone: 'err' | 'warn' | 'info'
}

function pickTrialsEndingSoon(t: HubTenantsResponse | null): TrialEntry[] {
  if (!t) return []
  const now = Date.now()
  const out: TrialEntry[] = []
  for (const tenant of t.tenants) {
    if (tenant.subscription_status !== 'trialing') continue
    if (!tenant.trial_ends_at) continue
    const ends = new Date(tenant.trial_ends_at).getTime()
    if (!isFinite(ends)) continue
    const diffMs = ends - now
    const days = Math.ceil(diffMs / DAY_MS)
    // Include expired (anything in the past) plus 0..7 days out.
    if (days > 7) continue

    let label: string
    let tone: 'err' | 'warn' | 'info'
    if (days < 0) {
      const ago = Math.abs(days)
      label = `expired ${ago}d ago`
      tone = 'err'
    } else if (days === 0) {
      label = 'today'
      tone = 'err'
    } else if (days <= 3) {
      label = `in ${days}d`
      tone = 'warn'
    } else {
      label = `in ${days}d`
      tone = 'info'
    }
    out.push({ tenant, daysRemaining: days, label, tone })
  }
  // Sort: most urgent first (expired → soonest ending).
  out.sort((a, b) => a.daysRemaining - b.daysRemaining)
  return out
}

function TrialsEndingTile({ trials }: { trials: TrialEntry[] }) {
  return (
    <Card
      title="Trials ending ≤7d"
      endpoint="/hub/tenants?status=trialing"
      style={{ gridColumn: 'span 5' }}
      bodyPad={false}
    >
      {trials.length === 0 && (
        <div style={{ padding: '16px 12px', color: 'var(--text-dim)', fontSize: 12 }}>
          No trials ending this week.
        </div>
      )}
      {trials.map((t, i) => {
        const dayColor =
          t.tone === 'err' ? 'var(--err)' :
          t.tone === 'warn' ? 'var(--warn)' :
          'var(--info)'
        return (
          <div
            key={t.tenant.company_id}
            className="row"
            style={{
              padding: '10px 12px',
              borderTop: i ? '1px solid var(--border)' : 0,
              gap: 10,
            }}
          >
            <StatusDot tone={t.tone} />
            <span style={{
              flex: 1, fontSize: 12.5,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {t.tenant.company_name}
            </span>
            <span className="mono" style={{ fontSize: 11, color: dayColor }}>
              {t.label}
            </span>
            <button
              type="button"
              className="btn ghost"
              style={{ height: 22, fontSize: 11 }}
              onClick={() => { /* visual stub — wiring deferred */ }}
            >
              Extend
            </button>
            <button
              type="button"
              className="btn primary"
              style={{ height: 22, fontSize: 11 }}
              onClick={() => { /* visual stub — wiring deferred */ }}
            >
              Convert
            </button>
          </div>
        )
      })}
    </Card>
  )
}

// ── Demo schedule · this week (col span 12) ────────────────────────────
type WeekDay = { label: string; key: string }

function buildCurrentWeek(): WeekDay[] {
  // Monday-anchored week. JS getDay(): Sun=0, Mon=1, ..., Sat=6.
  const now = new Date()
  const dow = now.getDay()
  const offsetToMonday = dow === 0 ? -6 : 1 - dow
  const monday = new Date(now)
  monday.setHours(0, 0, 0, 0)
  monday.setDate(monday.getDate() + offsetToMonday)

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

  const out: WeekDay[] = []
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday)
    d.setDate(monday.getDate() + i)
    const label = `${dayNames[d.getDay()]} ${monthNames[d.getMonth()]} ${d.getDate()}`
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
    out.push({ label, key })
  }
  return out
}

function DemoScheduleTile({ weekDays }: { weekDays: WeekDay[] }) {
  return (
    <Card
      title="Demo schedule · this week"
      endpoint="BACKEND_NEEDED: calendar"
      style={{ gridColumn: 'span 12' }}
      bodyPad={false}
    >
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
        gap: 1, background: 'var(--border)',
        borderBottomLeftRadius: 9, borderBottomRightRadius: 9, overflow: 'hidden',
      }}>
        {weekDays.map(d => (
          <div
            key={d.key}
            style={{
              background: 'var(--surface)',
              padding: 10,
              minHeight: 120,
            }}
          >
            <div className="mono" style={{ fontSize: 10, color: 'var(--text-dim)', marginBottom: 6 }}>
              {d.label}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-faint)' }}>—</div>
          </div>
        ))}
      </div>
    </Card>
  )
}

// ── helpers ────────────────────────────────────────────────────────────
function pickSandboxes(t: HubTenantsResponse | null): HubTenantSummary[] {
  if (!t) return []
  return t.tenants.filter(x => x.is_sandbox === true)
}
